/**
 * ============================================================
 *  GOOP ZONE — waveSystem.ts
 *  Wave Timing, Difficulty Scaling & Spawn Controller
 * ============================================================
 *
 *  Controls WHEN and HOW MANY enemies spawn during a run.
 *  Modeled after Megabonk's floor/wave system:
 *
 *    Floor 1: 10 minutes  (early game)
 *    Floor 2:  9 minutes  (mid game)
 *    Floor 3:  8 minutes  (late game)
 *    Final Swarm: infinite (after floor timer ends)
 *
 *  Key events during each floor:
 *    - Continuous enemy spawns that ramp up over time
 *    - Swarm waves at 3:00 and 7:00 into each floor
 *    - First boss at 3:00, second boss at 7:00-8:00
 *    - Portal boss when portal is activated
 *    - Final swarm after floor timer expires
 *
 *  Drop into: client/src/game/waveSystem.ts
 * ============================================================
 */

import {
  type EnemyDefinition,
  type EnemyVariant,
  getAvailableEnemies,
  ENEMY_DATABASE,
} from './enemies';

import {
  type EnemySystemState,
  type PlayerForEnemy,
  type CameraForEnemy,
  spawnEnemy,
} from './enemySystem';
import { getWaveSpawnRateMult } from './weaponLab';
import { rollRng } from './rng'; // BETA 20.94 — seeded balance rolls (reproducible enemy-type + elite spawn picks)

// ═══════════════════════════════════════════════════════════════
//  CONSTANTS
// ═══════════════════════════════════════════════════════════════

/** Floor durations in seconds */
const FLOOR_DURATIONS = [
  600,  // Floor 1: 10 minutes
  540,  // Floor 2:  9 minutes
  480,  // Floor 3:  8 minutes
];

/** Max number of floors before final swarm */
const MAX_FLOORS = 3;

/** Swarm event times (seconds into each floor) */
const SWARM_TIMES = [60, 180, 300, 420]; // 1:00, 3:00, 5:00, 7:00 — then every minute after 7

/** Boss spawn times (seconds into each floor) */
const BOSS_TIMES = [180, 480];  // 3:00 and 8:00

/** Swarm duration in seconds */
const SWARM_DURATION = 30;  // BETA 20.35 — Dylan: "the swarm for 30 seconds." Was 15. Steady linear baseline → 30s swarm wave → back to linear, repeating.

/** Swarm spawn multiplier (how many more enemies during a swarm) */
// BETA 20.71 — Dylan: "the 3 minute mark is a horde... but dont die just
// because im 3 minutes in." 123-run bot data: 102 deaths cluster at 180-209s
// — exactly the 3:00 swarm window. 5× was an un-telegraphed death wall.
// Megabonk/VS norm: hordes are a SWEAT you fight through (~2-3× baseline),
// telegraphed + escapable. Dropped 5 → 3; now also announced (enemyAdapter).
const SWARM_SPAWN_MULTIPLIER = 3;

/** Elite spawn chance (base). BETA 13.73: 2% → 4%. 77-run audit found ZERO elite
 *  deaths across 5 build archetypes — either elites are rare enough that the bot
 *  never sees them, or post-13.49 nerf made them killable before contact. Bumping
 *  base + per-minute scaling so cycle-2 has measurable elite-variant data. */
const ELITE_BASE_CHANCE = 0.02;  // BETA 14.98: 0.04 → 0.02 per PSBOT cyc-12 (Section D — named-elite frequency cut ~50% in 0-2min window). Pairs with new ramp rate 0.012/min so 2min = 4.4% (was 6%), 5min = 8% (was 9%).

/** Elite chance increase per minute */
const ELITE_CHANCE_PER_MINUTE = 0.012; // BETA 14.98: 0.01 → 0.012 (+1.2% per minute). Combined with lower base (0.02), the curve crosses old 4% at min ~1:40 instead of 0:00 — early game gets fewer elites, late game still hits the 0.20 cap by ~minute 15.

/** Max elite chance */
const ELITE_MAX_CHANCE = 0.20; // BETA 13.73: 0.15 → 0.20 (cap raised so late-game tests elite stats)

/** Max enemies alive at once — BETA 14.98 per-MINUTE curve per PSBOT RUN
 *  cyc-12 recommendation (was per-floor flat 120/160 from BETA 14.73). Early
 *  game gets breathing room so bots can clear, level up, pick weapons. Late
 *  game still hits the original cap.
 *
 *  Curve: 0:00-1:00 → 40, 1:00-2:00 → 70, 2:00-3:00 → 100, 3:00-5:00 → 140,
 *  5:00+ → 180. Floor 2+ adds 20 to baseline (slightly tougher floors).
 *
 *  This is a 3× reduction in 0-1 min vs the previous 120 cap. Goal: median
 *  bot survival 22-62s → 180s+ (3-min mark) with 4 weapons by 3:00. */
/** BETA 19.51 — Module-level cache of the player's current level. Updated
 *  by updateWaveSystem each frame so getMaxEnemiesAlive can read it
 *  without plumbing the parameter through every internal caller. Defaults
 *  to 1 (used pre-init or for legacy harness callers). */
let _currentPlayerLevel = 1;

// BETA 21.46 — GOD MODE (post-final-boss) density multiplier. Dylan: god mode
// must KILL you fast. A high-level player's alive-cap is time-bound at ~200, and
// their AoE clears that easily → infinite farm. Multiplying the cap in god mode
// dilutes their per-enemy DPS so enemies survive the cross and reach the player.
// Set each frame by the engine (1 = off outside god mode).
let _godModeDensityMult = 1;
export function setGodModeDensityMult(v: number): void {
  _godModeDensityMult = Number.isFinite(v) && v > 0 ? v : 1;
}

/**
 * BETA 19.51 — MEGABONK-STYLE CURVE BALANCE.
 *
 * Dylan live audit: "now balancing doesnt mean we have to change health
 * stats and such. the biggest problem would be how many enemies are on in
 * the game active at once at specific times. it takes so long to level up
 * to attack them and such. so you must do a hug research on mega bonk and
 * how its balanced and such. the goal isnt to just make wepons stronger
 * and enemies weaker and such. a true curve balance to it all."
 *
 * The Megabonk pattern: enemy spawn cap scales with PLAYER LEVEL, not raw
 * time. An under-leveled player gets fewer enemies on screen (room to
 * find weapons + level up); an over-leveled player gets pressure ramped
 * to match their power. This avoids the death-spiral where a slow-leveling
 * player gets buried by time-based density before they can build DPS.
 *
 * Final cap = min(timeCap, levelCap).
 *
 *   levelCap = 12 + level × 6        (L1: 18, L3: 30, L5: 42, L8: 60, L12: 84)
 *   timeCap  = previous curve         (0-1m: 30, 1-2m: 50, 2-3m: 80, ...)
 *
 * Examples:
 *   • L1 player at 0-1m → min(30, 18) = 18 enemies
 *   • L1 player at 2-3m → min(80, 18) = 18 enemies (no rush — get killing first)
 *   • L5 player at 2-3m → min(80, 42) = 42 enemies (level matched to time)
 *   • L8 player at 3-5m → min(140, 60) = 60 enemies
 *   • L20 player at 5+m → min(180+20, 132) = 132 enemies (level dominates)
 *
 * Once the player is L20+, the time-based cap takes over (max density
 * reached). Pre-L20 the level cap throttles the spawn pressure so the
 * player always has a fighting chance to grow into the floor.
 */
function getMaxEnemiesAlive(currentFloor: number, runMinutes: number): number {
  // Time-based cap (BETA 19.46 curve)
  let timeCap: number;
  if (runMinutes < 1)      timeCap = 30;
  else if (runMinutes < 2) timeCap = 50;
  else if (runMinutes < 3) timeCap = 80;
  else if (runMinutes < 5) timeCap = 140;
  else                     timeCap = 180;
  if (currentFloor >= 1) timeCap += 20;
  // BETA 20.77 — audit (Dylan's run): floor 2 opened with a 50-alive cap vs
  // his level-29 build = ghost town. Later floors keep a density FLOOR so
  // they never start emptier than the fight the player just left.
  timeCap = Math.max(timeCap, [30, 100, 140][Math.min(currentFloor, 2)]);

  // Level-based cap (BETA 19.51 Megabonk curve)
  // BETA 21.36 — Dylan: "not enough enemies early game, feels boring." The level cap
  // (binding constraint at low levels) was the throttle: L1 = 18 alive. Base 12 → 30
  // so early game has a real crowd (L1 = 36, L3 = 48) while late game stays time-capped.
  const level = Math.max(1, _currentPlayerLevel);
  const levelCap = 30 + level * 6;

  // Take the LOWER — under-leveled player throttled by level; high-level
  // player throttled by time. Either way the screen never floods past
  // what the player's current power can handle.
  // BETA 21.46 — except GOD MODE, where flooding past that is the POINT.
  return Math.round(Math.min(timeCap, levelCap) * _godModeDensityMult);
}

/** Boss spawn interval in seconds (before 10 min) */
const BOSS_INTERVAL_EARLY = 180;  // Every 3 minutes

/** Boss spawn interval in seconds (after 10 min) */
const BOSS_INTERVAL_LATE = 120;   // Every 2 minutes

/** Boss enemy IDs to alternate between */
// 23.55 — Timmy deleted (23.39 took CaseOh): the boss roster is EMPTY until
// the Phase C enemy-roster redo ships original bosses. The recurring-boss
// spawner below no-ops on an empty roster (guarded — empty % length is NaN).
const BOSS_ENEMY_IDS: readonly string[] = [];

/** Cleanup interval — remove dead enemies every N seconds */
const CLEANUP_INTERVAL = 2.0;

// ═══════════════════════════════════════════════════════════════
//  HORDE EVENTS
// ═══════════════════════════════════════════════════════════════
//  At specific minute marks within each floor, a sudden burst of
//  a single enemy type pours in from all sides. Hordes bypass the
//  normal per-type active-count cap so the flood actually happens.

// BETA 14.99 — HordeEvent supports TWO modes:
//   COUNT BURST (legacy): `count` set → burst-spawn N at the moment of trigger.
//   RATE BURST (new):     `rateMultiplier` + `duration` set → for `duration`
//     seconds starting at `time`, multiply spawn rate by `rateMultiplier` AND
//     force all spawns to be `enemyId` (zombie horde = only zombies). Honors
//     MAX_ENEMIES_ALIVE so the cap still gates the actual on-screen count.
//   A horde MUST set exactly one of (count) or (rateMultiplier + duration).
interface HordeEvent {
  time: number;             // Seconds into the current floor when triggered
  enemyId: string;          // Enemy type for the horde
  name: string;             // Warning banner label shown to the player
  count?: number;           // COUNT BURST: how many to spawn at once
  rateMultiplier?: number;  // RATE BURST: spawn-rate multiplier
  duration?: number;        // RATE BURST: seconds the boost lasts
}

// BETA 15.4 — REMOVED ZOMBIE RUSH at 1:30. User: "too many zombies
// spawning in at 1 minute. its like all the sudden 50 zombies.... why?"
// That was the legacy 50-zombie burst from BETA 13.49, predating the
// recent smooth-ramp design. With BETA 15.0's 30 zombies/first-60s curve
// + the BETA 14.99 ZOMBIE HORDE at 4:00, the 1:30 burst was redundant
// AND contradicted Dylan's "easy on-ramp" intent. Removed entirely.
//
// Current schedule (23.55 — Timmy slots now zombie placeholders):
//   - 4:00 ZOMBIE HORDE 4× rate / 30s — designed wave moment
//   - 5:00 ZOMBIE TSUNAMI 150 — coincides with Timmy regular spawning
//   - 7:00 TIMMY STAMPEDE 15 (23.39: was CaseOh)
//   - 9:00 TIMMY ARMY 50
// 23.55 — TIMMY DELETED: his three pressure slots (3:30 entry, 7:00 stampede,
// 9:00 army) are held by zombie bursts with PLACEHOLDER counts so the floor's
// difficulty cadence survives until the Phase C roster brings real mini-bosses.
// NEEDS DYLAN BALANCE SIGN-OFF before enemies re-enable (MAP_BUILD_NO_ENEMIES
// is on during the map build, so none of this fires today).
const HORDE_SCHEDULE: HordeEvent[] = [
  { time: 240, enemyId: 'zombie',                            rateMultiplier: 4,  duration: 30, name: 'ZOMBIE HORDE' },  // 4:00 — BETA 20.71: 10×→4× (10× was double the already-lethal 5× swarm; a sweat, not a wall)
  { time: 300, enemyId: 'zombie', count: 90,                                                   name: 'ZOMBIE TSUNAMI'   },  // 5:00 — BETA 20.71: 150→90 (cap-gated anyway; lower the headline burst)
  { time: 420, enemyId: 'zombie', count: 120,                                                  name: 'ZOMBIE STAMPEDE'  },  // 7:00 — placeholder for the deleted 15-Timmy squad
  { time: 540, enemyId: 'zombie', count: 180,                                                  name: 'ZOMBIE APOCALYPSE' },  // 9:00 — placeholder for the deleted 50-Timmy army
];

// ═══════════════════════════════════════════════════════════════
//  SPAWN RATE TABLE
// ═══════════════════════════════════════════════════════════════
//  Defines how many enemies spawn per second at each minute mark.
//  Values between entries are linearly interpolated.
//  Format: [minuteIntoRun, spawnsPerSecond]

const SPAWN_RATE_TABLE: [number, number][] = [
  // BETA 19.37 — Dylan: "there are way too many enemies so early. not a
  // good balance. look up mega bonk on how many enemies vs the timer".
  // Cowboy run died at 91s with only 12 kills — overwhelmed by min-2
  // pressure pile-up. Root cause: previous table jumped 0.95 → 2.55/sec
  // between min 1 and min 2 (2.5× density spike in 60s), then DOUBLED
  // AGAIN to 5.0 by min 3. Player couldn't level / pick perks before
  // the wall.
  //
  // Vampire-Survivors / Megabonk early-pacing principle: minutes 1-3
  // should be a SMOOTH SLOPE so players gain XP, level, build the deck.
  // Big density spikes belong on the HORDE_SCHEDULE (designed wave
  // events like 4:00 ZOMBIE HORDE), not the baseline ramp.
  //
  // New first-5-min curve (post 0.5 global mult):
  //   Min 0→1: 0.05 → 0.50  effective 0.025 → 0.25/sec  ~8 zombies/min
  //   Min 1→2: 0.50 → 1.20  effective 0.25  → 0.6/sec   ~25 zombies/min
  //   Min 2→3: 1.20 → 2.20  effective 0.6   → 1.1/sec   ~50 zombies/min
  //   Min 3→4: 2.20 → 2.80  effective 1.1   → 1.4/sec   ~75 zombies/min
  //   Min 4→5: 2.80 → 4.00  effective 1.4   → 2.0/sec   ~100 zombies/min
  // The 4:00 ZOMBIE HORDE rate-burst event still fires on top of this.
  // Min 6+ kept close to previous values — late game pressure preserved.
  // BETA 20.71 — Dylan: "only 3 or 4 enemies in the first 30 seconds. that is
  // lame. doesnt seem hard enough. i still feel like i am just able to go chest
  // to chest and shrine to shrine with no worries on enemies." The 19.37 cut
  // overcorrected into a ghost town. Raise early COUNT (more weak trash
  // streaming in from off-screen) — base DAMAGE stays locked-low, so it's
  // "busy + plentiful kills", not deadly. New values sit BELOW the old
  // death-at-91s curve (0.95 / 2.55 / 5.0 at min 1/2/3) but well above the
  // empty current one — smooth slope, NO cliff. Megabonk/VS early-pacing:
  // carpet the floor with squishy trash so the player levels fast and the
  // build comes online by the time the 3:00 horde hits.
  [0,    0.30],  // 20.71 was 0.05 — never-empty start (paired with accumulator prime 5)
  [0.5,  0.65],  // 20.71 NEW point — fast, smooth fill through the first 30s
  [1,    0.90],  // 20.71 was 0.50 — busy first minute (still under old 0.95)
  [2,    1.70],  // 20.71 was 1.20 — well under the old 2.55 wall
  [3,    2.50],  // 20.71 was 2.20 — smooth, far under old 5.0 cliff
  [4,    3.00],  // 20.71 was 2.80
  [5,    4.00],  // unchanged. Still building.
  [6,    6.00],  // was 7.0. Slight reduction for continuity.
  [7,    9.00],  // was 10.0. Second swarm preserved but slightly softer.
  [8,    7.00],  // unchanged — boss dip.
  [9,    9.50],  // was 10.0.
  [10,  12.00],  // was 13.0.
  [12,  15.00],  // was 16.0.
  [15,  19.00],  // was 20.0.
  [18,  24.00],  // was 25.0.
  [20,  29.00],  // was 30.0.
  [25,  39.00],  // was 40.0.
  [27,  49.00],  // was 50.0.
  [30,  64.00],  // was 65.0.
  [35,  79.00],  // was 80.0.
  [40,  99.00],  // was 100.0.
  [50, 129.00],  // was 130.0.
  [60, 159.00],  // was 160.0. Late game ~unchanged.
];

// ═══════════════════════════════════════════════════════════════
//  WAVE SYSTEM STATE
// ═══════════════════════════════════════════════════════════════

export interface WaveSystemState {
  // ── Floor Tracking ──
  currentFloor: number;           // 0-indexed (0 = Floor 1)
  floorStartTime: number;         // Run time when current floor started
  floorTimeElapsed: number;       // Seconds into current floor

  // ── Run Tracking ──
  runTimeSeconds: number;         // Total run time in seconds
  totalRunMinutes: number;        // Total run time in minutes (for spawn rate lookup)

  // ── Spawn Tracking ──
  spawnAccumulator: number;       // Fractional spawns accumulated
  lastCleanupTime: number;        // Last time dead enemies were cleaned up

  // ── Swarm State ──
  activeSwarm: boolean;           // Is a swarm event currently active?
  swarmEndTime: number;           // When the current swarm ends
  swarmTriggered: boolean[];      // Which swarm times have been triggered this floor
  repeatingSwarmMinute: number;   // Tracks which post-7min swarm last fired (0 = none)

  // ── Boss State ──
  bossTriggered: boolean[];       // Which boss times have been triggered this floor
  bossActive: boolean;            // Is a boss currently alive?
  nextBossSpawnTime: number;      // Run time (seconds) when next boss should spawn
  bossesSpawned: number;          // Total bosses spawned this run
  pendingBossCount: number;       // Extra bosses to add at next boss spawn (from activated boss shrines)

  // ── Horde State ──
  hordeTriggered: boolean[];      // One per HORDE_SCHEDULE entry, reset each floor

  // ── BETA 14.99: Rate-burst horde state (e.g. ZOMBIE HORDE 4:00 = 10× rate
  //  for 30s, only zombies). Set by horde processor when a horde with
  //  rateMultiplier+duration triggers. Cleared automatically when endTime
  //  passes. Only one rate burst can be active at a time (overwrites).
  activeRateBurstEnemyId: string | null;
  activeRateBurstMultiplier: number;
  activeRateBurstEndTime: number;

  // ── Final Swarm ──
  inFinalSwarm: boolean;          // Has the floor timer expired?
  finalSwarmTime: number;         // Seconds spent in final swarm

  // ── Difficulty ──
  difficultyMultiplier: number;   // From player stats/shrines (1.0 = normal)

  // ── Stats ──
  totalSpawned: number;
  totalSwarms: number;
}

/** Create a fresh wave system state for a new run */
export function createWaveSystemState(): WaveSystemState {
  return {
    currentFloor: 0,
    floorStartTime: 0,
    floorTimeElapsed: 0,

    runTimeSeconds: 0,
    totalRunMinutes: 0,

    // BETA 19.38 — Dylan: "we dont see our first enemy until like 30 seconds
    // into game. they should be much closer. off screen yes, but i should see
    // enemies at 1 second in". The min-0 spawn rate (0.025/sec effective)
    // gave an expected time-to-first-spawn of ~40s. Spawn DISTANCE is already
    // correct (50-150wu past the viewport edge → 0.6-1.8s walk-in at zombie
    // speed 85wu/s). Fix: prime the accumulator so the FIRST spawn fires
    // on the next update tick. The smooth ramp from BETA 19.37 still kicks
    // in normally from there. Net effect: first zombie visible by ~1-2s.
    // BETA 20.71 — Dylan: first 30s too empty. Prime an OPENING SPRINKLE
    // (~5 weak zombies stream in from the off-screen ring at t=0) so the
    // floor is never a ghost town. Megabonk/VS fire an opening event at 0:00.
    spawnAccumulator: 5.0,
    lastCleanupTime: 0,

    activeSwarm: false,
    swarmEndTime: 0,
    swarmTriggered: [false, false, false, false],
    repeatingSwarmMinute: 0,

    bossTriggered: [false, false],
    bossActive: false,
    // BETA 20.73 — Dylan-approved stagger: first boss 3:00 → 4:00. The 3:00
    // second used to triple-stack (swarm at SWARM_TIMES[1]=180 + this boss
    // landing the same tick) — 123/123 bot deaths clustered there. Boss now
    // arrives after the swarm clears and after TIMMY ENTRY (3:30) introduces
    // the type.
    nextBossSpawnTime: 240,   // First boss at 4:00
    bossesSpawned: 0,
    pendingBossCount: 0,

    hordeTriggered: HORDE_SCHEDULE.map(() => false),

    // BETA 14.99 — rate-burst horde initial state (none active)
    activeRateBurstEnemyId: null,
    activeRateBurstMultiplier: 1.0,
    activeRateBurstEndTime: 0,

    inFinalSwarm: false,
    finalSwarmTime: 0,

    difficultyMultiplier: 1.0,

    totalSpawned: 0,
    totalSwarms: 0,
  };
}

// ═══════════════════════════════════════════════════════════════
//  UPDATE (called every frame from engine.ts)
// ═══════════════════════════════════════════════════════════════

export interface WaveUpdateResult {
  spawnedCount: number;
  swarmStarted: boolean;
  swarmEnded: boolean;
  bossSpawnRequested: boolean;    // Engine should spawn a boss
  floorComplete: boolean;         // Floor timer expired, portal should appear
  bossIndex: number;              // Which boss (0 = first, 1 = second)
  hordeStarted: boolean;          // A horde event just triggered
  hordeName: string;              // Banner label for the horde
}

/**
 * Update the wave system. Determines how many enemies to spawn this frame.
 * Actually spawns them via enemySystem.spawnEnemy().
 *
 * @param dt - Delta time in seconds
 * @param runTimeSeconds - Total seconds elapsed in this run
 */
export function updateWaveSystem(
  waveState: WaveSystemState,
  enemyState: EnemySystemState,
  dt: number,
  runTimeSeconds: number,
  // BETA 23.6x — CO-OP PHASE 2 part A: all live player views (SP = 1).
  // waveState itself STAYS a world singleton (Bram's explicit ask — never
  // per-player-ize wave scheduling). Part A consumes the primary view only
  // (byte-identical in SP); part B adds live-count spawn scaling + the
  // level-aggregate input (numbers = Dylan's balance pass, OPEN).
  // BETA 23.6x — CO-OP PHASE 2 part C audit: everything below is scheduling
  // + NEW-spawn placement (spawnEnemy → getSpawnPosition ring around the
  // primary camera). It never relocates or culls an existing enemy, so per
  // the §4 minimum it stays primary-relative; the multi-player spawn-ring
  // policy is Dylan's OPEN #3 (per-player rings vs centroid vs nearest) and
  // Bram's E5 SpawnSources inherits this players[] plumbing.
  players: PlayerForEnemy[],
  camera: CameraForEnemy,
  isBlocked?: (x: number, y: number, size: number) => boolean,
): WaveUpdateResult {
  const player = players[0];
  const result: WaveUpdateResult = {
    spawnedCount: 0,
    swarmStarted: false,
    swarmEnded: false,
    bossSpawnRequested: false,
    floorComplete: false,
    bossIndex: -1,
    hordeStarted: false,
    hordeName: '',
  };

  // ── Update timers ──
  waveState.runTimeSeconds = runTimeSeconds;
  waveState.totalRunMinutes = runTimeSeconds / 60;
  waveState.floorTimeElapsed = runTimeSeconds - waveState.floorStartTime;
  // BETA 20.77 — THE CLOCK FIX. state.time no longer resets on floor advance
  // (it corrupted floorTimeElapsed to NEGATIVE values all of floor 2 — no
  // swarms/hordes/bosses, unreachable portal). Spawn BUILD-UP is now driven
  // by FLOOR-LOCAL minutes (Dylan: "another build up just like the first")
  // with a per-floor HEAD START so later floors skip the empty on-ramp and
  // start already-busy (floor 2 ≈ the min-2.5 curve, floor 3 ≈ min-4).
  const _floorLocalMin = (Math.max(0, waveState.floorTimeElapsed)
    + [0, 150, 240][Math.min(waveState.currentFloor, 2)]) / 60;

  // BETA 19.51 — Cache the player's current level so getMaxEnemiesAlive can
  // gate spawn density on player power (Megabonk curve balance). Defaults
  // to 1 when the field is missing (lab harnesses, init frames).
  _currentPlayerLevel = (player as PlayerForEnemy & { level?: number }).level ?? 1;

  // ── Check floor completion ──
  const floorDuration = getFloorDuration(waveState.currentFloor);
  if (!waveState.inFinalSwarm && waveState.floorTimeElapsed >= floorDuration) {
    waveState.inFinalSwarm = true;
    result.floorComplete = true;
  }

  if (waveState.inFinalSwarm) {
    waveState.finalSwarmTime += dt;
  }

  // ── Check swarm triggers (fixed: 1, 3, 5, 7 min) ──
  for (let i = 0; i < SWARM_TIMES.length; i++) {
    if (!waveState.swarmTriggered[i] && waveState.floorTimeElapsed >= SWARM_TIMES[i]) {
      waveState.swarmTriggered[i] = true;
      waveState.activeSwarm = true;
      waveState.swarmEndTime = runTimeSeconds + SWARM_DURATION;
      waveState.totalSwarms++;
      result.swarmStarted = true;
    }
  }

  // ── Repeating swarms every minute after 7:00 ──
  if (waveState.floorTimeElapsed > 420) {
    const minutePast7 = Math.floor((waveState.floorTimeElapsed - 420) / 60) + 1;
    if (minutePast7 > waveState.repeatingSwarmMinute) {
      waveState.repeatingSwarmMinute = minutePast7;
      waveState.activeSwarm = true;
      waveState.swarmEndTime = runTimeSeconds + SWARM_DURATION;
      waveState.totalSwarms++;
      result.swarmStarted = true;
    }
  }

  // ── Check swarm end ──
  if (waveState.activeSwarm && runTimeSeconds >= waveState.swarmEndTime) {
    waveState.activeSwarm = false;
    result.swarmEnded = true;
  }

  // ── Check boss triggers ──
  for (let i = 0; i < BOSS_TIMES.length; i++) {
    if (!waveState.bossTriggered[i] && waveState.floorTimeElapsed >= BOSS_TIMES[i]) {
      waveState.bossTriggered[i] = true;
      result.bossSpawnRequested = true;
      result.bossIndex = i;
    }
  }

  // ── Check horde triggers ──
  {
    // BETA 13.70 FPS: O(1) maintained counter instead of O(n) filter.
    const aliveNow = enemyState.aliveCount;
    for (let i = 0; i < HORDE_SCHEDULE.length; i++) {
      const horde = HORDE_SCHEDULE[i];
      if (!waveState.hordeTriggered[i] && waveState.floorTimeElapsed >= horde.time) {
        waveState.hordeTriggered[i] = true;
        result.hordeStarted = true;
        result.hordeName = horde.name;

        if (typeof horde.count === 'number') {
          // ── COUNT BURST: spawn N at the trigger moment ──
          const spawnable = Math.min(horde.count, getMaxEnemiesAlive(waveState.currentFloor, (Math.max(0, waveState.floorTimeElapsed) + [0, 150, 240][Math.min(waveState.currentFloor, 2)]) / 60) - aliveNow);
          for (let j = 0; j < spawnable; j++) {
            const spawned = spawnEnemy(
              enemyState,
              horde.enemyId,
              'normal',
              player,
              camera,
              runTimeSeconds,
              isBlocked,
              true, // ignoreMaxCount — hordes must always flood in
            );
            if (spawned) waveState.totalSpawned++;
          }
        } else if (typeof horde.rateMultiplier === 'number' && typeof horde.duration === 'number') {
          // ── RATE BURST: activate a sustained spawn-rate multiplier window
          //  for `duration` seconds. While active: spawn rate × multiplier
          //  AND all spawns forced to `enemyId` (e.g. ZOMBIE HORDE = only
          //  zombies for 30s). Honors MAX_ENEMIES_ALIVE so the cap still
          //  gates on-screen count. BETA 14.99.
          waveState.activeRateBurstEnemyId = horde.enemyId;
          waveState.activeRateBurstMultiplier = horde.rateMultiplier;
          waveState.activeRateBurstEndTime = runTimeSeconds + horde.duration;
        }
      }
    }
  }

  // BETA 14.99 — Auto-clear rate burst when its window expires. Resets to
  // multiplier 1.0 so the spawn-rate calc returns to normal seamlessly.
  if (waveState.activeRateBurstEnemyId !== null && runTimeSeconds >= waveState.activeRateBurstEndTime) {
    waveState.activeRateBurstEnemyId = null;
    waveState.activeRateBurstMultiplier = 1.0;
  }

  // ── Recurring boss spawns (23.55: roster empty until Phase C bosses) ──
  if (BOSS_ENEMY_IDS.length > 0 && runTimeSeconds >= waveState.nextBossSpawnTime) {
    // BETA 17.78 — Dylan: "something is bugged" (boss pile-up). If existing
    // bosses haven't been killed yet, skip this spawn cycle. Cap concurrent
    // bosses at 2. The previous code spawned a new boss every 2-3 minutes
    // regardless of how many were still alive, so a long lab session or
    // bot run where bosses survive accumulates them. Player's DPS can't
    // catch up at late-game ENDLESS scaling → infinite pile-up.
    const concurrentBosses = enemyState.enemies.filter(e =>
      e.alive && e.variant === 'boss'
    ).length;
    const MAX_CONCURRENT_BOSSES = 2;
    if (concurrentBosses >= MAX_CONCURRENT_BOSSES) {
      // Delay next attempt by 30 sec — give player time to kill existing bosses.
      waveState.nextBossSpawnTime = runTimeSeconds + 30;
      return result;
    }
    // E1 (enemy rebuild) — the roster is EMPTY today (length-guarded above, so
    // this branch is unreachable); the modulo survives so the Phase C boss
    // roster drops back in with one array edit.
    const bossIndex = waveState.bossesSpawned % BOSS_ENEMY_IDS.length;
    const bossId = BOSS_ENEMY_IDS[bossIndex];
    const bossDef = ENEMY_DATABASE[bossId];

    // 1 normal boss + 1 extra per activated boss shrine, clamped to remaining
    // headroom under MAX_CONCURRENT_BOSSES (so pendingBossCount can't bypass).
    const headroom = Math.max(0, MAX_CONCURRENT_BOSSES - concurrentBosses);
    const requested = 1 + (waveState.pendingBossCount ?? 0);
    const totalToSpawn = Math.min(requested, headroom);
    waveState.pendingBossCount = 0; // consume the pending count

    if (bossDef) {
      for (let b = 0; b < totalToSpawn; b++) {
        const spawned = spawnEnemy(
          enemyState,
          bossId,
          'boss',
          player,
          camera,
          runTimeSeconds,
          isBlocked,
          true, // ignoreMaxCount — boss must always be able to spawn regardless of horde overflow
        );
        if (spawned) {
          waveState.bossesSpawned++;
          waveState.totalSpawned++;
        }
      }
    }

    // Schedule next boss — shorter interval past 10 minutes
    const interval = runTimeSeconds >= 600 ? BOSS_INTERVAL_LATE : BOSS_INTERVAL_EARLY;
    waveState.nextBossSpawnTime = runTimeSeconds + interval;
  }

  // ── Calculate spawn rate ──
  // BETA 20.77 — floor-local minutes (+ head start), not total-run minutes:
  // each floor is its own build-up per Dylan's spec.
  const baseSpawnRate = getSpawnRate(_floorLocalMin);
  // BETA 14.42 — wave spawn rate user-tunable via WeaponLab slider for
  // continuous-stream-density testing (Vampire Survivors style). Default 1.0.
  // BETA 16.91 — PSBOT cycle-30i §3C + Dylan "yes": global 0.5×
  // multiplier on spawn rate. Pairs with 2× HP / 1.55× damage in
  // enemies.ts getScaledStats. Same DPS pressure, half the entities.
  const SPAWN_GLOBAL_MULT = 0.55;   // BETA 21.21 — 0.5 → 0.55 (Dylan fairness pass: a small +10% density bump for a busier swarm; the speed RAMP does the heavy lifting on strong builds)
  let effectiveSpawnRate = baseSpawnRate * waveState.difficultyMultiplier * getWaveSpawnRateMult() * SPAWN_GLOBAL_MULT;

  // Swarm multiplier
  if (waveState.activeSwarm) {
    effectiveSpawnRate *= SWARM_SPAWN_MULTIPLIER;
  }

  // Final swarm escalation
  if (waveState.inFinalSwarm) {
    const finalSwarmMultiplier = 1.0 + waveState.finalSwarmTime * 0.1; // +10% per second
    effectiveSpawnRate *= Math.min(finalSwarmMultiplier, 10.0); // Cap at 10x
  }

  // BETA 14.99 — Rate-burst horde multiplier (e.g. ZOMBIE HORDE = 10× for
  // 30s, only zombies). When active, the spawn rate is multiplied AND all
  // spawns are forced to the burst's enemy type (handled below via
  // forcedEnemyId). MAX_ENEMIES_ALIVE still caps the on-screen count.
  if (waveState.activeRateBurstEnemyId !== null) {
    effectiveSpawnRate *= waveState.activeRateBurstMultiplier;
  }

  // ── Performance cap ── (BETA 13.70 FPS: O(1) counter)
  if (enemyState.aliveCount >= getMaxEnemiesAlive(waveState.currentFloor, _floorLocalMin)) {
    effectiveSpawnRate = 0;
  }

  // BETA 21.52 — Dylan: "there should NOT be any enemies on my screen when I start
  // floor 2/3/4." A ~4s opening grace on every advanced floor (currentFloor is
  // 0-indexed → >=1 is floor 2+) so you arrive to an EMPTY floor and it builds up
  // from there. Floor 1 (run start) keeps its normal opening. Paired with the
  // spawnAccumulator prime being zeroed on floor advance (engine) so nothing
  // bursts the instant the grace ends.
  const FLOOR_START_GRACE_SEC = 5;  // ~2.5s covers the floor-transition overlay + ~2.5s of visibly-empty play
  if (waveState.currentFloor >= 1 && (waveState.floorTimeElapsed ?? 0) < FLOOR_START_GRACE_SEC) {
    effectiveSpawnRate = 0;
  }

  // ── Accumulate and spawn ──
  waveState.spawnAccumulator += effectiveSpawnRate * dt;

  while (waveState.spawnAccumulator >= 1.0) {
    waveState.spawnAccumulator -= 1.0;

    // BETA 14.99 — During a rate-burst horde, force the burst's enemy type
    // (e.g. ZOMBIE HORDE = only zombies). Bypasses pickEnemyToSpawn weights.
    let enemyDef;
    if (waveState.activeRateBurstEnemyId !== null) {
      enemyDef = ENEMY_DATABASE[waveState.activeRateBurstEnemyId];
      if (!enemyDef) continue;
    } else {
      // Pick which enemy to spawn (respects maxActiveCount per type)
      enemyDef = pickEnemyToSpawn(runTimeSeconds, enemyState);
      if (!enemyDef) continue;
    }

    // Determine variant (normal vs elite)
    const variant = rollEliteVariant(waveState.totalRunMinutes);

    // Spawn it
    const spawned = spawnEnemy(
      enemyState,
      enemyDef.id,
      variant,
      player,
      camera,
      runTimeSeconds,
      isBlocked,
    );

    if (spawned) {
      waveState.totalSpawned++;
      result.spawnedCount++;
    }
  }

  // ── Periodic cleanup ──
  if (runTimeSeconds - waveState.lastCleanupTime >= CLEANUP_INTERVAL) {
    waveState.lastCleanupTime = runTimeSeconds;
    // Caller (engine.ts) should call cleanupDead(enemyState)
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════
//  FLOOR MANAGEMENT
// ═══════════════════════════════════════════════════════════════

/** Advance to the next floor (called when player enters portal) */
export function advanceFloor(waveState: WaveSystemState, runTimeSeconds: number): void {
  waveState.currentFloor = Math.min(waveState.currentFloor + 1, MAX_FLOORS - 1);
  waveState.floorStartTime = runTimeSeconds;
  waveState.floorTimeElapsed = 0;
  waveState.inFinalSwarm = false;
  waveState.finalSwarmTime = 0;
  waveState.swarmTriggered = [false, false, false, false];
  waveState.repeatingSwarmMinute = 0;
  waveState.bossTriggered = [false, false];
  waveState.bossActive = false;
  waveState.hordeTriggered = HORDE_SCHEDULE.map(() => false);
}

/** Get the duration of a floor in seconds */
export function getFloorDuration(floor: number): number {
  if (floor >= 0 && floor < FLOOR_DURATIONS.length) {
    return FLOOR_DURATIONS[floor];
  }
  return FLOOR_DURATIONS[FLOOR_DURATIONS.length - 1]; // Default to last floor duration
}

/** Get the remaining time on the current floor in seconds */
export function getFloorTimeRemaining(waveState: WaveSystemState): number {
  if (waveState.inFinalSwarm) return 0;
  const duration = getFloorDuration(waveState.currentFloor);
  return Math.max(0, duration - waveState.floorTimeElapsed);
}

/** Format time remaining as MM:SS string */
export function formatTimeRemaining(waveState: WaveSystemState): string {
  const remaining = getFloorTimeRemaining(waveState);
  if (waveState.inFinalSwarm) {
    const swarmTime = Math.floor(waveState.finalSwarmTime);
    const min = Math.floor(swarmTime / 60);
    const sec = swarmTime % 60;
    return `SWARM +${min}:${sec.toString().padStart(2, '0')}`;
  }
  const min = Math.floor(remaining / 60);
  const sec = Math.floor(remaining % 60);
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

// ═══════════════════════════════════════════════════════════════
//  DIFFICULTY
// ═══════════════════════════════════════════════════════════════

/** Set the difficulty multiplier (from shrines, items, etc.) */
export function setDifficultyMultiplier(waveState: WaveSystemState, multiplier: number): void {
  waveState.difficultyMultiplier = Math.max(0.1, multiplier);
}

/** Add to difficulty multiplier */
export function addDifficulty(waveState: WaveSystemState, amount: number): void {
  waveState.difficultyMultiplier = Math.max(0.1, waveState.difficultyMultiplier + amount);
}

// ═══════════════════════════════════════════════════════════════
//  INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Interpolate the spawn rate from the SPAWN_RATE_TABLE.
 */
function getSpawnRate(minutes: number): number {
  // Before first entry
  if (minutes <= SPAWN_RATE_TABLE[0][0]) {
    return SPAWN_RATE_TABLE[0][1];
  }

  // After last entry
  const last = SPAWN_RATE_TABLE[SPAWN_RATE_TABLE.length - 1];
  if (minutes >= last[0]) {
    return last[1];
  }

  // Find the two entries to interpolate between
  for (let i = 0; i < SPAWN_RATE_TABLE.length - 1; i++) {
    const [t0, r0] = SPAWN_RATE_TABLE[i];
    const [t1, r1] = SPAWN_RATE_TABLE[i + 1];
    if (minutes >= t0 && minutes < t1) {
      const t = (minutes - t0) / (t1 - t0);
      return r0 + (r1 - r0) * t;
    }
  }

  return SPAWN_RATE_TABLE[0][1];
}

/**
 * Pick which enemy type to spawn based on weighted random selection.
 * Only considers enemies whose minSpawnTime has been reached AND maxActiveCount not hit.
 */
function pickEnemyToSpawn(runTimeSeconds: number, enemyState: EnemySystemState): EnemyDefinition | null {
  const available = getAvailableEnemies(runTimeSeconds).filter(e => {
    if (e.maxActiveCount <= 0) return true; // 0 = unlimited
    const activeCount = enemyState.enemies.filter(
      en => en.alive && en.definitionId === e.id
    ).length;
    return activeCount < e.maxActiveCount;
  });
  if (available.length === 0) return null;

  // Weighted random selection
  const totalWeight = available.reduce((sum, e) => sum + e.spawnWeight, 0);
  let roll = rollRng() * totalWeight; // BETA 20.94 — seeded enemy-type pick

  for (const enemy of available) {
    roll -= enemy.spawnWeight;
    if (roll <= 0) {
      return enemy;
    }
  }

  return available[0]; // Fallback
}

/**
 * Roll whether a spawned enemy should be an elite variant.
 */
function rollEliteVariant(runMinutes: number): EnemyVariant {
  const chance = Math.min(
    ELITE_BASE_CHANCE + ELITE_CHANCE_PER_MINUTE * runMinutes,
    ELITE_MAX_CHANCE,
  );
  return rollRng() < chance ? 'elite' : 'normal'; // BETA 20.94 — seeded elite/normal pick
}

// ═══════════════════════════════════════════════════════════════
//  DEBUG / INFO
// ═══════════════════════════════════════════════════════════════

/** Get a debug summary of the wave system state */
export function getWaveDebugInfo(waveState: WaveSystemState, enemyState: EnemySystemState): string {
  const aliveCount = enemyState.aliveCount; // BETA 13.70 FPS: O(1) maintained counter
  const spawnRate = getSpawnRate(waveState.totalRunMinutes);

  return [
    `Floor: ${waveState.currentFloor + 1}/${MAX_FLOORS}`,
    `Time: ${formatTimeRemaining(waveState)}`,
    `Alive: ${aliveCount}/${getMaxEnemiesAlive(waveState.currentFloor, waveState.totalRunMinutes)}`,
    `Spawn/s: ${(spawnRate * waveState.difficultyMultiplier).toFixed(1)}`,
    `Swarm: ${waveState.activeSwarm ? 'ACTIVE' : 'no'}`,
    `Final: ${waveState.inFinalSwarm ? 'YES' : 'no'}`,
    `Difficulty: ${(waveState.difficultyMultiplier * 100).toFixed(0)}%`,
    `Kills: ${enemyState.totalKills}`,
    `Spawned: ${waveState.totalSpawned}`,
  ].join(' | ');
}
